<template>
  <el-popover
    :key="popoverKey"
    placement="right"
    width="400"
    trigger="hover"
    :content="null"
  >
    <template #reference>
      <button class="btn btn-info btn-sm" :title="t('common.viewDetails')"><i class="fas fa-info-circle"></i></button>
    </template>
    <div class="popover-detail-card">
      <div class="popover-title">{{ type === 'vm' ? t('vm.details') : t('disk.details') }}</div>
      <template v-if="type === 'vm'">
        <div><b>{{ t('vm.name') }}：</b>{{ item.name }}</div>
        <div><b>{{ t('vm.zone') }}：</b>{{ item.zone }}</div>
        <div><b>{{ t('common.status') }}：</b>{{ getVMStatus(item) }}</div>
        <div><b>{{ t('vm.machineType') }}：</b>{{ getMachineType(item) }}</div>
        <div><b>{{ t('vm.internalIP') }}：</b>{{ getInternalIP(item) }}</div>
        <div><b>{{ t('vm.externalIP') }}：</b>{{ getExternalIP(item) }}</div>
        <div v-if="!item.isOnPremise"><b>{{ t('vm.systemDisk') }}：</b>{{ item.disks?.[0]?.deviceName || t('vm.systemDisk') }}</div>
        <div v-if="!item.isOnPremise"><b>{{ t('vm.attachedDisks') }}：</b>
          <span v-if="!item.disks || item.disks.length <= 1">{{ t('common.none') }}</span>
          <ul v-else>
            <li v-for="d in item.disks.slice(1)" :key="d.deviceName">{{ d.deviceName }}</li>
          </ul>
        </div>

        <!-- SSH 命令区域 -->
        <div class="ssh-section">
          <div><b>{{ t('vm.sshCommand') }}：</b></div>
          <div class="ssh-command-container">
            <el-input
              v-model="sshCommand"
              readonly
              size="small"
              class="ssh-command-input"
            >
              <template #append>
                <el-button
                  size="small"
                  @click="copySshCommand"
                  :disabled="!sshCommand"
                >
                  {{ t('common.copy') }}
                </el-button>
              </template>
            </el-input>
          </div>
          <div class="ssh-note">
            <small v-if="item.isOnPremise">
              <i class="fas fa-info-circle"></i> {{ t('vm.ssmSessionManager') }}
            </small>
            <small v-else-if="hasExternalIP">
              <i class="fas fa-info-circle"></i> {{ t('vm.hasExternalIP') }}
            </small>
            <small v-else>
              <i class="fas fa-info-circle"></i> {{ t('vm.noExternalIP') }}
            </small>
          </div>
        </div>
      </template>
      <template v-else>
        <div><b>{{ t('disk.name') }}：</b>{{ item.name }}</div>
        <div><b>{{ t('disk.zone') }}：</b>{{ item.zone }}</div>
        <div><b>{{ t('common.status') }}：</b>{{ getDiskStatus(item) }}</div>
        <div><b>{{ t('disk.size') }}：</b>{{ (item.sizeGb || item.size) }} GB</div>
        <div><b>{{ t('disk.type') }}：</b>{{ getDiskType(item) }}</div>
        <div><b>{{ t('disk.attachedVM') }}：</b>
          <span v-if="!item.attachedTo || item.attachedTo.length === 0">{{ t('common.none') }}</span>
          <ul v-else>
            <li v-for="attachment in item.attachedTo" :key="attachment.instanceId || attachment.instanceName">
              {{ getAttachedVMName(attachment) }} ({{ attachment.device || 'unknown' }})
            </li>
          </ul>
        </div>
      </template>
    </div>
  </el-popover>
</template>

<script setup>
import { computed } from 'vue';
import { ElMessage } from 'element-plus';
import { useI18n } from 'vue-i18n';
import { useCloudProvider } from '../composables/useCloudProvider.js';

const { currentProvider } = useCloudProvider();

const { t } = useI18n();

const props = defineProps({
  type: { type: String, required: true },
  item: { type: Object, required: true },
  currentProjectId: { type: String, required: true }
});

const popoverKey = computed(() => `${props.item.name}-${props.type}-info`);

// 检查是否有外部 IP
const hasExternalIP = computed(() => {
  if (props.type !== 'vm') {return false;}
  const externalIP = getExternalIP(props.item);
  return externalIP && externalIP !== 'N/A';
});

// 生成 SSH 命令
const sshCommand = computed(() => {
  if (props.type !== 'vm') {return '';}
  const vm = props.item;

  if (currentProvider.value === 'aws') {
    if (vm.isOnPremise) {
      // AWS SSM Session Manager for on-premise instances
      let ssmCommand = `aws ssm start-session --target ${vm.id} --region ${vm.region}`;
      
      // 检查是否有 Document 标签，如果有则添加 --document 参数
      if (vm.labels && vm.labels.Document) {
        ssmCommand += ` --document ${vm.labels.Document}`;
      }
      
      return ssmCommand;
    } else {
      // AWS EC2 Instance Connect for EC2 instances
      return `aws ec2-instance-connect ssh --instance-id ${vm.id} --region ${vm.region} --os-user ubuntu`;
    }
  } else {
    // GCP SSH 命令
    const baseCmd = `gcloud compute ssh ${vm.name} --zone ${vm.zone} --project ${props.currentProjectId}`;
    if (hasExternalIP.value) {
      return baseCmd;
    } else {
      return `${baseCmd} --tunnel-through-iap`;
    }
  }
});

// 复制 SSH 命令
const copySshCommand = async () => {
  try {
    await navigator.clipboard.writeText(sshCommand.value);
    ElMessage.success(t('vm.sshCommandCopied'));
  } catch (error) {
    console.error(t('common.copyFailed'), error);
    ElMessage.error(t('common.copyFailed'));
  }
};

function getVMStatus(vm) {
  if (vm && (vm.status === 'RUNNING' || vm.status === 'running')) {return t('vm.status.running');}
  if (vm && vm.status === 'STOPPED') {return t('vm.status.stopped');}
  if (vm && vm.status === 'TERMINATED') {return t('vm.status.terminated');}
  if (vm && vm.status === 'SUSPENDED') {return t('vm.status.suspended');}
  if (vm && vm.status === 'SUSPENDING') {return t('vm.status.suspending');}
  if (vm && vm.status === 'STOPPING') {return t('vm.status.stopping');}
  return (vm && vm.status) ? vm.status : t('vm.status.unknown');
}

// 获取机器类型
const getMachineType = (vm) => {
  if (currentProvider.value === 'aws') {
    return vm.instanceType ?? t('vm.na');
  } else {
    return vm.machineType?.split('/').pop() ?? t('vm.na');
  }
};

// 获取内部IP
const getInternalIP = (vm) => {
  if (currentProvider.value === 'aws') {
    return vm?.privateIP ?? t('vm.na');
  } else {
    return vm?.networkInterfaces?.[0]?.networkIP ?? t('vm.na');
  }
};

// 获取外部IP
const getExternalIP = (vm) => {
  if (currentProvider.value === 'aws') {
    return vm?.publicIP ?? t('vm.na');
  } else {
    return vm?.networkInterfaces?.[0]?.accessConfigs?.[0]?.natIP ?? t('vm.na');
  }
};

function getDiskStatus(disk) {
  if (disk && disk.status === 'READY') {return t('disk.status.ready');}
  if (disk && disk.status === 'CREATING') {return t('disk.status.creating');}
  if (disk && disk.status === 'RESTORING') {return t('disk.status.restoring');}
  return (disk && disk.status) ? disk.status : t('disk.status.unknown');
}
function getDiskType(disk) {
  return disk && disk.type?.split('/').pop() || 'N/A';
}

// 获取磁盘挂载的VM名称
function getAttachedVMName(attachment) {
  if (currentProvider.value === 'aws') {
    // AWS: 返回实例ID，因为我们在ListView中已经处理了ID到名称的转换
    return attachment.instanceId || 'Unknown';
  } else {
    // GCP: 返回实例名称
    return attachment.instanceName || 'Unknown';
  }
}
</script>

<style scoped>
.popover-detail-card {
  font-size: 1rem;
  color: #333;
  min-width: 220px;
  max-width: 400px;
  line-height: 1.7;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(80,80,80,0.10);
  padding: 1rem 1.2rem;
}
.popover-title {
  font-weight: bold;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #396afc;
}

.ops-agent-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

.ops-agent-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #666;
  font-size: 0.9rem;
}

.ops-agent-status {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.ops-agent-warning {
  color: #e6a23c;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.ops-agent-warning i {
  color: #e6a23c;
}

.ssh-section {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

.ssh-command-container {
  margin: 0.5rem 0;
}

.ssh-command-input {
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
}

.ssh-note {
  margin-top: 0.5rem;
  color: #666;
}

.ssh-note small {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.ssh-note i {
  color: #409eff;
}
</style>
